import React, { FC, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { NgButton } from '../../../UI';
import { buttonVariants, buttonWidths } from '@clatter/ui';
import { CheckCircleIcon, WarningIcon } from '../../../UI/NgIcons';

export interface PublishWebsiteAccessOption {
  type: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  body?: React.ReactNode;
  onPublish: (navType: string) => void;
}

export interface PublishWebsiteNavOption {
  type: string;
  label: string;
  description: string;
}

interface PublishWebsitePanelProps {
  open: boolean;
  title: string;
  subtitle?: string;
  anchor?: 'left' | 'right';
  onClose: () => void;
  onBack?: () => void;
  onUnpublish: () => void;
  onAccessTypeChange?: (type: string) => void;
  onInternalNavChange?: (type: string) => void;
  isPublished?: boolean;
  isReady?: boolean;
  defaultAccessType?: string;
  defaultNavType?: string;
  previewLink?: string;
  accessOptions: PublishWebsiteAccessOption[];
  internalNavOptions?: PublishWebsiteNavOption[] | null;
  isLoading?: boolean;
}

const StyledOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 400;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`;

const StyledPanel = styled.div<{ $open: boolean; $anchor: 'left' | 'right' }>`
  position: fixed;
  top: ${({ theme }) => theme.pageHeader.height};
  ${({ $anchor }) => $anchor === 'left' ? 'left: var(--side-nav-width, 55px);' : 'right: 0;'}
  bottom: 0;
  width: 420px;
  background: ${({ theme }) => theme.palette.cardBackground};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: 500;
  display: flex;
  flex-direction: column;
  transform: ${({ $open, $anchor }) => $open ? 'translateX(0)' : $anchor === 'left' ? 'translateX(-100%)' : 'translateX(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const StyledPanelHeader = styled.div`
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.cardBorder};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const StyledPanelTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.textStrong};
`;

const StyledPanelSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.textMuted};
  margin-top: 2px;
`;

const StyledCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.textMuted};
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.palette.stroke};
    color: ${({ theme }) => theme.palette.textStrong};
  }
`;

const StyledPanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const StyledBanner = styled.div<{ $warning: boolean }>`
  background: ${({ $warning }) => ($warning ? '#FFFBEB' : '#EFF6FF')};
  border: 1px solid ${({ $warning }) => ($warning ? '#FDE68A' : '#BFDBFE')};
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const StyledBannerIcon = styled.div<{ $warning: boolean }>`
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  color: ${({ $warning, theme }) => ($warning ? theme.palette.warning : theme.palette.linkHoverColor)};

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StyledBannerTitle = styled.div<{ $warning: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $warning }) => ($warning ? '#92400E' : '#0052CC')};
  margin-bottom: 2px;
`;

const StyledBannerDesc = styled.div<{ $warning: boolean }>`
  font-size: 12px;
  color: ${({ $warning }) => ($warning ? '#B45309' : '#4D94FF')};
  line-height: 1.4;
`;

const StyledSectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.textStrong};
  margin-bottom: 4px;
`;

const StyledSectionHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.textMuted};
  margin-bottom: 14px;
  line-height: 1.4;
`;

const StyledAccessCard = styled.div<{ $selected: boolean; $disabled: boolean }>`
  border: 1.5px solid ${({ $selected }) => ($selected ? '#0A6EFF' : '#E5E7EB')};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ $selected }) => ($selected ? '#F0F7FF' : 'white')};
  transition: border-color 0.15s, background 0.15s, opacity 0.15s;
  margin-bottom: 8px;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? '#0A6EFF' : '#D1D5DB')};
  }
`;

const StyledAccessCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StyledRadio = styled.div<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $selected }) => ($selected ? '#0A6EFF' : '#D1D5DB')};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0a6eff;
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
  }
`;

const StyledAccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.inputBorder};

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StyledAccessLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.textStrong};
  margin-bottom: 2px;
`;

const StyledAccessDesc = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.textDimmed}; // #4b5563; /
  line-height: 1.4;
`;

const StyledAccessBody = styled.div`
  margin-top: 12px;
`;

const StyledNavSectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.textStrong};
  margin-top: 24px;
  margin-bottom: 4px;
`;

const StyledNavSectionHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.textMuted};
  margin-bottom: 12px;
  line-height: 1.4;
`;

const StyledNavContainer = styled.div`
  background: ${({ theme }) => theme.palette.white};
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
`;

const StyledNavDivider = styled.div`
  height: 1px;
  background: #f3f4f6;
  margin: 0 14px;
`;

const StyledNavOption = styled.div<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border: 1.5px solid ${({ $selected }) => ($selected ? '#BFDBFE' : 'transparent')};
  background: ${({ $selected }) => ($selected ? '#F0F7FF' : 'transparent')};
  transition: background 0.12s;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    background: ${({ $selected }) => ($selected ? '#F0F7FF' : '#F9FAFB')};
  }
`;

const StyledNavRadio = styled.div<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $selected }) => ($selected ? '#0A6EFF' : '#D1D5DB')};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0a6eff;
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
  }
`;

const StyledNavLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.textStrong};
  margin-bottom: 1px;
`;

const StyledNavDesc = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.textDimmed};
  line-height: 1.4;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const StyledSpinner = styled.span<{ $light?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $light }) => ($light ? 'rgba(255,255,255,0.35)' : 'rgba(75,85,99,0.25)')};
  border-top-color: ${({ $light, theme }) => ($light ? 'white' : theme.palette.textDimmed)};
  animation: ${spin} 0.6s linear infinite;
  flex-shrink: 0;
`;

const StyledPanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.palette.cardBorder};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledPreviewLink = styled.a`
  font-size: 12px;
  color: #0a6eff;
  text-align: center;
  text-decoration: none;
  font-family: inherit;

  &:hover {
    opacity: 0.75;
  }
`;

const StyledBackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  padding: 0;
  text-align: center;
  font-family: inherit;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    color: ${({ theme }) => theme.palette.textStrong};
  }
`;

export const PublishWebsitePanel: FC<PublishWebsitePanelProps> = ({
  open,
  title,
  subtitle,
  anchor = 'right',
  onClose,
  onBack,
  onUnpublish,
  onAccessTypeChange,
  onInternalNavChange,
  isPublished,
  isReady = true,
  defaultAccessType,
  defaultNavType,
  previewLink,
  accessOptions,
  internalNavOptions,
  isLoading,
}) => {
  const [selectedAccessType, setSelectedAccessType] = useState(defaultAccessType || accessOptions[0]?.type);
  const [selectedNavType, setSelectedNavType] = useState(defaultNavType || internalNavOptions?.[0]?.type);

  const selectedOption = accessOptions.find((option) => option.type === selectedAccessType);

  useEffect(() => {
    if (open) {
      setSelectedAccessType(defaultAccessType || accessOptions[0]?.type);
      setSelectedNavType(defaultNavType || internalNavOptions?.[0]?.type);
    }
  }, [open, defaultAccessType]);

  return (
    <>
      <StyledOverlay $open={open} onClick={onClose} />

      <StyledPanel $open={open} $anchor={anchor}>
        <StyledPanelHeader>
          <div>
            <StyledPanelTitle>{title}</StyledPanelTitle>
            {subtitle && <StyledPanelSubtitle>{subtitle}</StyledPanelSubtitle>}
          </div>

          <StyledCloseButton onClick={onClose} aria-label="Close panel">
            <CloseIcon style={{ fontSize: 16 }} />
          </StyledCloseButton>
        </StyledPanelHeader>

        <StyledPanelBody>
          {!isPublished && (
            <StyledBanner $warning={!isReady}>
              <StyledBannerIcon $warning={!isReady}>
                {isReady ? <CheckCircleIcon /> : <WarningIcon />}
              </StyledBannerIcon>

              <div>
                <StyledBannerTitle $warning={!isReady}>
                  {isReady ? 'Ready to Publish' : 'Missing data'}
                </StyledBannerTitle>
                <StyledBannerDesc $warning={!isReady}>
                  {isReady
                    ? 'Configure access and publishing options before your site goes live.'
                    : 'Please fill out values form to publish website.'}
                </StyledBannerDesc>
              </div>
            </StyledBanner>
          )}

          <StyledSectionLabel>Access</StyledSectionLabel>
          <StyledSectionHint>Choose how visitors access this site.</StyledSectionHint>

          {accessOptions.map((option) => {
            const isSelected = option.type === selectedAccessType;
            return (
              <StyledAccessCard
                key={option.type}
                $selected={isSelected}
                $disabled={!!isLoading}
                onClick={() => {
                  setSelectedAccessType(option.type);
                  onAccessTypeChange?.(option.type);
                }}
              >
                <StyledAccessCardTop>
                  <StyledRadio $selected={isSelected} />
                  <StyledAccessIcon>{option.icon}</StyledAccessIcon>
                  <div>
                    <StyledAccessLabel>{option.label}</StyledAccessLabel>
                    <StyledAccessDesc>{option.description}</StyledAccessDesc>
                  </div>
                </StyledAccessCardTop>
                {isSelected && option.body && (
                  <StyledAccessBody onClick={(e) => e.stopPropagation()}>{option.body}</StyledAccessBody>
                )}
              </StyledAccessCard>
            );
          })}

          {internalNavOptions && internalNavOptions.length > 0 && (
            <>
              <StyledNavSectionLabel>Internal Navigation</StyledNavSectionLabel>

              <StyledNavSectionHint>Control how this site appears within Clatter for your team.</StyledNavSectionHint>

              <StyledNavContainer>
                {internalNavOptions.map((option, index) => {
                  const isSelected = option.type === selectedNavType;
                  return (
                    <React.Fragment key={option.type}>
                      {index > 0 && <StyledNavDivider />}
                      <StyledNavOption
                        $selected={isSelected}
                        $disabled={!!isLoading || !isPublished}
                        onClick={() => {
                          setSelectedNavType(option.type);
                          onInternalNavChange?.(option.type);
                        }}
                      >
                        <StyledNavRadio $selected={isSelected} />
                        <div>
                          <StyledNavLabel>{option.label}</StyledNavLabel>
                          <StyledNavDesc>{option.description}</StyledNavDesc>
                        </div>
                      </StyledNavOption>
                    </React.Fragment>
                  );
                })}
              </StyledNavContainer>
            </>
          )}
        </StyledPanelBody>

        <StyledPanelFooter>
          <NgButton
            width={buttonWidths.full}
            variant={isPublished ? buttonVariants.clear : buttonVariants.primary}
            isLoading={isLoading}
            onClick={() => (isPublished ? onUnpublish() : selectedOption?.onPublish(selectedNavType || ''))}
            disabled={isLoading}
          >
            {isPublished ? 'Unpublish' : ' Publish Site'}
          </NgButton>

          {previewLink && (
            <StyledPreviewLink href={previewLink} target="_blank" rel="noopener noreferrer">
              Preview
            </StyledPreviewLink>
          )}

          <StyledBackButton onClick={onBack || onClose} disabled={isLoading}>
            Back to settings
          </StyledBackButton>
        </StyledPanelFooter>
      </StyledPanel>
    </>
  );
};
